import {
  ApiNotification,
  ApplicantsByListingNotification,
  MentionListingApplicationNotification,
  MentionsByApplicationNotification,
  MissingProfileSummaryNotification,
  NewListingApplicationNotification,
  NewSuggestedApplicantsByJobNotification,
  NewSuggestedApplicantsNotification,
  Notification,
} from 'api/types/ApiNotification';
import { sortBy } from 'utils/functional/array';

export function sortNotifications(
  unsortedNotifications: ApiNotification[]
): Notification[] {
  const {
    applicantNotifications,
    mentionNotifications,
    suggestedApplicantsNotifications,
    missingProfSummaryNotifications,
  } = unsortedNotifications.reduce(
    (acc, notification) => {
      if (notification.type === 'NEW_LISTING_APPLICATION') {
        acc.applicantNotifications.push(notification);
      } else if (notification.type === 'MENTION_LISTING_APPLICATION') {
        acc.mentionNotifications.push(notification);
      } else if (notification.type === 'NEW_SUGGESTED_APPLICANTS') {
        acc.suggestedApplicantsNotifications.push(notification);
      } else if (notification.type === 'MISSING_PROF_SUMMARY') {
        acc.missingProfSummaryNotifications.push(notification);
      }
      return acc;
    },
    {
      applicantNotifications: [] as NewListingApplicationNotification[],
      mentionNotifications: [] as MentionListingApplicationNotification[],
      suggestedApplicantsNotifications:
        [] as NewSuggestedApplicantsNotification[],
      missingProfSummaryNotifications:
        [] as MissingProfileSummaryNotification[],
    }
  );

  const applicantNotificationsByListingId: Record<
    string,
    ApplicantsByListingNotification
  > = applicantNotifications.reduce(
    (acc: Record<string, ApplicantsByListingNotification>, notification) => {
      const notificationListing = notification.data.listingApplication.listing;
      const notificationForListing = acc[notificationListing.id] || {
        type: 'NEW_APPLICATIONS_BY_LISTING',
        data: {
          ...notificationListing,
          applications: [],
          orgId: notification.about.id,
          status: notification.data.listingApplication.status,
          count: 0,
        },
      };
      notificationForListing.data.count += 1;
      notificationForListing.created =
        acc[notificationListing.id] &&
        new Date(acc[notificationListing.id].created) >
          new Date(notification.created)
          ? acc[notificationListing.id].created
          : notification.created;
      notificationForListing.data.applications.push({
        id: notification.data.listingApplication.id,
        notificationId: notification.id,
      });
      return {
        ...acc,
        [notificationForListing.data.id]: notificationForListing,
      };
    },
    {}
  );

  const mentionNotificationsByApplicationId: Record<
    string,
    MentionsByApplicationNotification
  > = mentionNotifications.reduce(
    (acc: Record<string, MentionsByApplicationNotification>, notification) => {
      const notificationApplication = notification.data.listingApplication;

      const notificationForApplication = acc[notificationApplication.id] || {
        type: 'NEW_MENTIONS_BY_APPLICATION',
        data: {
          ...notificationApplication,
          mentions: [],
          orgId: notification.about.id,
          status: notification.data.listingApplication.status,
          count: 0,
        },
      };
      notificationForApplication.data.count += 1;
      notificationForApplication.created =
        acc[notificationApplication.id] &&
        new Date(acc[notificationApplication.id].created) >
          new Date(notification.created)
          ? acc[notificationApplication.id].created
          : notification.created;
      notificationForApplication.data.mentions.push({
        id: notification.data.listingApplication.id,
        notificationId: notification.id,
      });
      return {
        ...acc,
        [notificationForApplication.data.id]: notificationForApplication,
      };
    },
    {}
  );

  const suggestedApplicantNotificationsByJobId: Record<
    string,
    NewSuggestedApplicantsByJobNotification
  > = suggestedApplicantsNotifications.reduce(
    (
      acc: Record<string, NewSuggestedApplicantsByJobNotification>,
      notification
    ) => {
      const { job } = notification.data.suggestedApplicantBatch;

      const notificationForJob = acc[job.id] || {
        type: 'NEW_SUGGESTED_APPLICANTS_BY_JOB',
        data: {
          job,
          orgId: notification.about.id,
          count: 0,
          batches: [],
        },
      };
      notificationForJob.data.count +=
        notification.data.suggestedApplicantBatch.applicantCount;
      notificationForJob.created =
        acc[job.id] &&
        new Date(acc[job.id].created) > new Date(notification.created)
          ? acc[job.id].created
          : notification.created;
      notificationForJob.data.batches.push({
        id: notification.data.suggestedApplicantBatch.id,
        notificationId: notification.id,
      });
      return { ...acc, [notificationForJob.data.job.id]: notificationForJob };
    },
    {}
  );

  const notifications: Notification[] = [
    ...missingProfSummaryNotifications,
    ...Object.values(applicantNotificationsByListingId),
    ...Object.values(mentionNotificationsByApplicationId),
    ...Object.values(suggestedApplicantNotificationsByJobId),
  ];

  return sortBy(notifications, (n) => new Date(n.created), true);
}
